import {AlertTriangle, BriefcaseBusiness, Clock} from "lucide-react";
import {Link} from "react-router-dom";

import {Badge} from "@/components/ui/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";

import {PROPERTY_TIER_LABELS, PROPERTY_TIER_ORDER} from "../lib";
import type {DashboardProperties, DashboardPropertyTier} from "../types";

type PropertiesCardProps = {
  properties: DashboardProperties;
};

const TIER_BADGE_CLASS: Record<DashboardPropertyTier, string> = {
  paid: "bg-primary/10 text-primary border-primary/20",
  free_limited: "bg-muted text-muted-foreground border-border",
  free_inspection: "bg-muted text-muted-foreground border-border",
};

export function PropertiesCard({properties}: PropertiesCardProps) {
  const expiring = properties.expiring_soon_items ?? [];

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-foreground text-sm font-semibold">Properties</CardTitle>
        <Badge variant="outline" className="font-normal">
          Company-wide
        </Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2">
          {PROPERTY_TIER_ORDER.map((tier) => {
            const data = properties.by_tier?.[tier];
            const active = data?.active ?? 0;
            const expiringSoon = data?.expiring_soon ?? 0;
            return (
              <li
                key={tier}
                className="flex items-center justify-between gap-3 rounded-md border p-2.5"
              >
                <span className="flex items-center gap-2">
                  <BriefcaseBusiness className="text-muted-foreground size-4" />
                  <span className="text-foreground text-sm font-medium">
                    {PROPERTY_TIER_LABELS[tier]}
                  </span>
                  <Badge variant="outline" className={cn("text-[10px]", TIER_BADGE_CLASS[tier])}>
                    {tier}
                  </Badge>
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-foreground tabular-nums text-sm font-semibold">
                    {active.toLocaleString()}
                  </span>
                  {expiringSoon > 0 ? (
                    <span className="text-amber-700 dark:text-amber-400 flex items-center gap-1 text-xs font-medium">
                      <Clock className="size-3" />
                      {expiringSoon} expiring
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>

        {expiring.length > 0 ? (
          <div className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 flex flex-col gap-2 rounded-md border p-3">
            <span className="text-amber-800 dark:text-amber-300 flex items-center gap-1.5 text-xs font-medium">
              <AlertTriangle className="size-3.5" />
              Expiring soon
            </span>
            <ul className="flex flex-col gap-1">
              {expiring.slice(0, 4).map((item, index) => (
                <li
                  key={`${item.id || "property"}-${index}`}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <Link
                    to={`/properties/${item.id}`}
                    className="text-foreground hover:text-primary min-w-0 truncate font-medium"
                  >
                    {item.name}
                  </Link>
                  <span className="text-muted-foreground tabular-nums">
                    {item.days_until_expiration != null
                      ? `${item.days_until_expiration}d`
                      : (item.expires_at ?? "—")}
                  </span>
                </li>
              ))}
              {expiring.length > 4 ? (
                <li className="text-muted-foreground text-xs">
                  +{expiring.length - 4} more
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
